export type CaptureStatus = 'idle' | 'requesting' | 'active' | 'interrupted' | 'error'

export interface CaptureInfo {
  readonly width: number
  readonly height: number
  readonly label: string
}

export class CaptureService extends EventTarget {
  readonly video: HTMLVideoElement
  private stream: MediaStream | null = null
  status: CaptureStatus = 'idle'
  lastError: string | null = null

  constructor() {
    super()
    this.video = document.createElement('video')
    this.video.muted = true
    this.video.playsInline = true
    this.video.autoplay = true
  }

  async selectWindow(): Promise<CaptureInfo> {
    if (!navigator.mediaDevices?.getDisplayMedia) throw new Error('当前浏览器不支持窗口捕获')
    this.stop()
    this.setStatus('requesting')
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: { ideal: 30, max: 60 } },
        audio: false,
      })
      const track = stream.getVideoTracks()[0]
      if (!track) throw new Error('未获得视频轨道')
      this.stream = stream
      this.video.srcObject = stream
      track.addEventListener('ended', () => {
        this.setStatus('interrupted')
        this.dispatchEvent(new Event('ended'))
      }, { once: true })
      await this.video.play()
      await this.waitForDimensions()
      this.setStatus('active')
      const settings = track.getSettings()
      return {
        width: this.video.videoWidth || settings.width || 0,
        height: this.video.videoHeight || settings.height || 0,
        label: track.label || 'MapleStory',
      }
    } catch (error) {
      this.lastError = error instanceof Error ? error.message : String(error)
      this.setStatus('error')
      throw error
    }
  }

  get mediaStream(): MediaStream | null {
    return this.stream
  }

  async snapshot(): Promise<ImageBitmap> {
    if (this.status !== 'active' || this.video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) throw new Error('捕获画面尚未就绪')
    return createImageBitmap(this.video)
  }

  stop(): void {
    for (const track of this.stream?.getTracks() ?? []) track.stop()
    this.stream = null
    this.video.srcObject = null
    if (this.status !== 'idle') this.setStatus('idle')
  }

  private setStatus(status: CaptureStatus): void {
    this.status = status
    this.dispatchEvent(new CustomEvent('status', { detail: status }))
  }

  private async waitForDimensions(): Promise<void> {
    if (this.video.videoWidth && this.video.videoHeight) return
    await new Promise<void>((resolve, reject) => {
      const timer = window.setTimeout(() => reject(new Error('等待捕获画面超时')), 8_000)
      this.video.addEventListener('loadedmetadata', () => {
        window.clearTimeout(timer)
        resolve()
      }, { once: true })
    })
  }
}

export const captureService = new CaptureService()

